import React, { useEffect, useState } from 'react';

import { correctingTrailingNumbers } from '@cryptoboyio/mercuryo-lab/utils';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import {
  bemCssModules,
  getCurrencySymbol,
  getFormattedFiatCurrencyNumber,
  getTestId,
} from '@utils';

import { useStore, walletActions } from '@modules';
import { CryptoCurrencyEnum, CurrencyEnum, FiatCurrencyEnum } from '@modules/global/types';
import { GETRateResponse, RateTypesEnum } from '@modules/wallet/types';

import { RateType } from '@containers/Converter/useConverterState';
import { isEEACompliant } from '@selectors';
import { getOperations } from '@selectors';

import { useCustomContext } from '../OperationBlock/context';
import { DataKeysEnum } from '../OperationBlock/types';

import style from './Details.scss';

const cn = bemCssModules(style, 'Details');
const ti = getTestId('Details');

const { cards, blockchain_wallets, my_accounts } = DataKeysEnum;

export type TDetails = {
  from: CurrencyEnum;
  fromAmount?: string;
  to?: CurrencyEnum;
  operationRate?: RateType;
  onlyRate?: boolean;
  withRateDetails?: boolean;
  withAdditionalFee?: boolean;
};

const Details: CustomFC<TDetails> = props => {
  const {
    className,
    from,
    fromAmount,
    to,
    operationRate,
    onlyRate,
    withRateDetails = false,
    withAdditionalFee,
  } = props;

  const { right } = useCustomContext();

  const { t } = useTranslation();
  const [store, dispatch] = useStore();
  const isEEA = useSelector(isEEACompliant);

  const { global } = store;
  const { fiat, config } = global.currencies;
  const { display_options } = config;
  const operations = getOperations(store);
  const [infoRate, setInfoRate] = useState<GETRateResponse>();

  const fiatCurrency = ((fiat.find(cur => cur === from) ? from : to) ||
    CONST.defaultFiatCurrency) as FiatCurrencyEnum;
  const cryptoCurrency = ((from === fiatCurrency ? to : from) ||
    CONST.defaultCryptoCurrency) as CryptoCurrencyEnum;
  const isHiddenBaseRate = isEEA && !(Number(fromAmount) > 0);

  const rate = correctingTrailingNumbers(
    operationRate?.rate || infoRate?.rate || '',
    display_options[fiatCurrency]?.display_digits,
    display_options[fiatCurrency]?.total_digits
  );

  useEffect(() => {
    if (from && to && !operationRate) {
      const isSell =
        right?.group && [cards, blockchain_wallets, my_accounts].includes(right?.group);
      const type = isSell ? RateTypesEnum.sell : RateTypesEnum.buy;
      const data = { from, to, type };
      dispatch(walletActions.getRateForRateInformer(data))
        .unwrap()
        .then(res => {
          setInfoRate(res.data);
        });
    }
  }, [from, to, operationRate]);

  const display = (currency: CurrencyEnum, amount?: string) => {
    if (isHiddenBaseRate) {
      const currencySymbol = getCurrencySymbol(currency);
      return `${currencySymbol} --`;
    }

    let result = '';

    if (fiat.includes(currency as FiatCurrencyEnum)) {
      result = getFormattedFiatCurrencyNumber({
        amount: +(amount || 0),
        currency: currency as FiatCurrencyEnum,
      });
      if (!amount) result = result.replace(/0(\.0+)?/g, '--');
    } else {
      const value =
        amount && typeof +amount === 'number'
          ? correctingTrailingNumbers(
              amount || 0,
              display_options[currency]?.display_digits,
              display_options[currency]?.total_digits
            )
          : '--';
      result = `${value} ${currency}`;
    }

    return result;
  };

  const sellOrWithdraw = operations.withdraw || operations.sell;
  const feeCurrency = sellOrWithdraw ? cryptoCurrency : fiatCurrency;
  const additionalFeeCurrency = sellOrWithdraw ? fiatCurrency : cryptoCurrency;
  const additionalFeeRate = operationRate?.fee[additionalFeeCurrency];

  return (
    <div className={cn(null, [className, 'small'])} data-testid={ti()}>
      {withRateDetails ? (
        <>
          <div>{t('rate-info.rate')}</div>
          <div>{operationRate?.rate}</div>
          {operationRate?.subtotal[cryptoCurrency]} {cryptoCurrency} ={' '}
          <div>{display(fiatCurrency, rate)}</div>
        </>
      ) : (
        <>
          <div>1 {cryptoCurrency} =</div>
          <div>{display(fiatCurrency, rate)}</div>
        </>
      )}
      {!onlyRate && (
        <>
          <div>{t('rate-info.fee')}</div>
          <div>
            {withAdditionalFee && additionalFeeRate && (
              <span className={cn('additional-fee')}>
                {display(additionalFeeCurrency, additionalFeeRate)}
              </span>
            )}
            {display(feeCurrency, operationRate?.fee[feeCurrency])}
          </div>
          <div>{t('rate-info.total')}</div>
          <div>{display(feeCurrency, operationRate?.total[feeCurrency])}</div>
        </>
      )}
    </div>
  );
};

export default Details;
